import React, { useEffect, useMemo, useRef, useState } from "react";
import { Clapperboard } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../components/layout/view/ViewHeader";
import Text from "../../../../components/typography/Text";

import SearchField from "../../../../components/input/searchField/SearchField";
import Button from "../../../../components/buttons/Button";
import EmptyState from "../../../../components/feedback/EmptyState";
import FormModal from "../../../../components/modals/FormModal";
import ContentGrid from "../../../../components/grids/content/ContentGrid";

import PopThumbnailCard from "../../../../components/cards/pops/PopThumbnailCard";
import CreatorPopEditForm from "../forms/CreatorPopEditForm";
import CreatorPopsFeed from "./CreatorPopsFeed";

import { useCreatorPops } from "../hooks/useCreatorPops";
import { useUserPopsViews } from "../../users/views/hooks/useUserPopsViews";

import "./CreatorPopsView.css";

const LIMIT = 12;
const MAX_VIEW_IDS_PER_REQUEST = 50;

const validId = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const id = Number(value);

  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const normalizeCount = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const count = Number(value);

  return Number.isFinite(count) && count >= 0 ? count : fallback;
};

const normalizeViewResult = (result) => {
  const popId = validId(result?.popId ?? result?.pop_id);

  if (!popId) return null;

  const realViews = normalizeCount(
    result.realViews ?? result.real_views ?? result.views,
  );

  const demoViews = normalizeCount(result.demoViews ?? result.demo_views);

  return {
    popId,
    realViews,
    demoViews,
    totalViews: normalizeCount(
      result.totalViews ?? result.total_views,
      realViews + demoViews,
    ),
  };
};

export default function CreatorPopsView({ username }) {
  const {
    pops,
    total,
    loading,
    loadingMore,
    searching,
    deleting,
    error,
    hasMore,
    getCreatorPops,
    loadMore,
    refresh,
    searchCreatorPops,
    deleteCreatorPop,
  } = useCreatorPops(username, LIMIT);

  const { getPopViewCounts, getPopViewCount } = useUserPopsViews();

  const [activeModal, setActiveModal] = useState(null);
  const [selectedPopTag, setSelectedPopTag] = useState(null);
  const [selectedPopId, setSelectedPopId] = useState(null);

  const [searchValue, setSearchValue] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [viewCounts, setViewCounts] = useState({});

  const loadedViewIdsRef = useRef(new Set());
  const pendingViewIdsRef = useRef(new Set());
  const getPopViewCountsRef = useRef(getPopViewCounts);
  const mountedRef = useRef(false);
  const generationRef = useRef(0);
  const closePlayerRef = useRef(null);
  const loadViewCountsRef = useRef(null);

  const popItems = useMemo(() => {
    const seen = new Set();

    return (Array.isArray(pops) ? pops : []).filter((pop) => {
      const popId = validId(pop?.popId);

      if (!popId || seen.has(popId)) return false;

      seen.add(popId);
      return true;
    });
  }, [pops]);

  const hasPops = popItems.length > 0;
  const isBusy = loading || loadingMore || searching || deleting;
  const isPlayerOpen = selectedPopId !== null;

  const errorMessage = error
    ? typeof error === "string"
      ? error
      : error?.message || "Unable to load Pops."
    : "";

  getPopViewCountsRef.current = getPopViewCounts;

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    generationRef.current += 1;
    loadedViewIdsRef.current.clear();
    pendingViewIdsRef.current.clear();

    setViewCounts({});
    setSelectedPopId(null);
    setSelectedPopTag(null);
    setActiveModal(null);
    setSearchValue("");
    setActiveSearch("");
  }, [username]);

  const loadViewCounts = async (
    ids,
    { force = false, generation = generationRef.current } = {},
  ) => {
    const validIds = [...new Set(ids.map(validId).filter(Boolean))];

    const requestedIds = validIds.filter((id) => {
      const key = String(id);

      if (pendingViewIdsRef.current.has(key)) return false;

      return force || !loadedViewIdsRef.current.has(key);
    });

    if (!requestedIds.length) return;

    const batches = [];

    for (
      let index = 0;
      index < requestedIds.length;
      index += MAX_VIEW_IDS_PER_REQUEST
    ) {
      batches.push(requestedIds.slice(index, index + MAX_VIEW_IDS_PER_REQUEST));
    }

    requestedIds.forEach((id) => {
      pendingViewIdsRef.current.add(String(id));
    });

    await Promise.all(
      batches.map(async (batch) => {
        try {
          const results = await getPopViewCountsRef.current(batch);

          if (!mountedRef.current || generation !== generationRef.current) {
            return;
          }

          if (!Array.isArray(results)) {
            throw new Error("Invalid creator Pop view-count response.");
          }

          const nextCounts = {};

          results.forEach((result) => {
            const normalized = normalizeViewResult(result);

            if (!normalized) return;

            const key = String(normalized.popId);

            nextCounts[key] = normalized;
            loadedViewIdsRef.current.add(key);
          });

          if (Object.keys(nextCounts).length) {
            setViewCounts((previous) => ({
              ...previous,
              ...nextCounts,
            }));
          }
        } catch (viewError) {
          console.error(
            "Unable to retrieve creator Pop view counts:",
            viewError,
          );
        } finally {
          if (generation === generationRef.current) {
            batch.forEach((id) => {
              pendingViewIdsRef.current.delete(String(id));
            });
          }
        }
      }),
    );
  };

  loadViewCountsRef.current = loadViewCounts;

  useEffect(() => {
    if (!popItems.length) return;

    void loadViewCountsRef.current(popItems.map((pop) => pop.popId));
  }, [popItems, username]);

  const refreshViewCounts = async () => {
    if (!popItems.length) return;

    await loadViewCounts(
      popItems.map((pop) => pop.popId),
      { force: true },
    );
  };

  const handleSearchSubmit = async (event) => {
    event?.preventDefault?.();

    const value = searchValue.trim();

    if (!value) {
      setActiveSearch("");
      await getCreatorPops({ reset: true });
      return;
    }

    const result = await searchCreatorPops(value);

    if (result !== false) {
      setActiveSearch(value);
    }
  };

  const handleSearchChange = (event) => {
    const value =
      typeof event === "string" ? event : (event?.target?.value ?? "");

    setSearchValue(value);

    if (!value.trim() && activeSearch) {
      setActiveSearch("");
      void getCreatorPops({ reset: true });
    }
  };

  const handleOpenEditModal = (popTag) => {
    if (!popTag) return;

    setSelectedPopId(null);
    setSelectedPopTag(popTag);
    setActiveModal("edit");
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setSelectedPopTag(null);
  };

  const handleOpenPop = (pop) => {
    const popId = validId(pop?.popId);

    if (!popId) return;

    setSelectedPopId(popId);
  };

  const handleClosePop = () => {
    setSelectedPopId(null);
    void refreshViewCounts();
  };

  closePlayerRef.current = handleClosePop;

  useEffect(() => {
    if (!isPlayerOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closePlayerRef.current?.();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isPlayerOpen]);

  useEffect(() => {
    if (selectedPopId === null || loading || searching) return;

    const exists = popItems.some(
      (pop) => String(pop.popId) === String(selectedPopId),
    );

    if (!exists) {
      setSelectedPopId(null);
    }
  }, [popItems, selectedPopId, loading, searching]);

  const handleRefresh = async () => {
    if (activeSearch) {
      await searchCreatorPops(activeSearch);
    } else {
      await refresh();
    }

    await refreshViewCounts();
  };

  const handleDeletePop = async (popTag) => {
    if (!popTag || deleting) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this Pop?",
    );

    if (!confirmed) return;

    try {
      const deletedPop = popItems.find((pop) => pop.popTag === popTag);

      const result = await deleteCreatorPop(popTag);

      if (result === false) return;

      if (deletedPop) {
        const key = String(deletedPop.popId);

        loadedViewIdsRef.current.delete(key);
        pendingViewIdsRef.current.delete(key);

        setViewCounts((previous) => {
          const next = { ...previous };
          delete next[key];
          return next;
        });

        if (String(deletedPop.popId) === String(selectedPopId)) {
          setSelectedPopId(null);
        }
      }
    } catch (deleteError) {
      console.error("Unable to delete Pop:", deleteError);
    }
  };

  const handleMoreClick = (pop) => {
    if (!pop?.popTag) return;

    handleOpenEditModal(pop.popTag);
  };

  const handleVideoError = (pop, videoError) => {
    console.error("Pop playback error:", pop?.popId, videoError);
  };

  const handleFeedLoadMore = () => {
    if (activeSearch || loading || loadingMore || !hasMore) {
      return false;
    }

    return loadMore();
  };

  const renderPop = (pop) => {
    const fetched = viewCounts[String(pop.popId)];

    const fallbackRealViews = normalizeCount(
      pop.realViews ?? pop.real_views ?? pop.views,
    );

    const realViews = fetched
      ? fetched.realViews
      : getPopViewCount(pop.popId, fallbackRealViews);

    const demoViews = fetched
      ? fetched.demoViews
      : normalizeCount(pop.demoViews ?? pop.demo_views);

    const totalViews = fetched
      ? fetched.totalViews
      : normalizeCount(
          pop.totalViews ?? pop.total_views,
          realViews + demoViews,
        );

    const displayPop = {
      ...pop,
      views: totalViews,
      realViews,
      demoViews,
      totalViews,
    };

    return (
      <PopThumbnailCard
        key={pop.popId ?? pop.popTag}
        pop={displayPop}
        mode="grid"
        onClick={() => handleOpenPop(pop)}
        onEdit={() => handleOpenEditModal(pop.popTag)}
        onDelete={() => handleDeletePop(pop.popTag)}
      />
    );
  };

  const renderContent = () => {
    if ((loading || searching) && !hasPops) {
      return (
        <ContentGrid
          items={[]}
          loading
          columns={6}
          tabletColumns={3}
          mobileColumns={2}
          gap={16}
          skeletonCount={LIMIT}
        />
      );
    }

    if (errorMessage && !hasPops) {
      return (
        <EmptyState
          icon={<Clapperboard size={28} />}
          title="Unable to load Pops"
          description={errorMessage}
          action={
            <Button
              value="Try Again"
              variant="primary-outlined"
              onClick={handleRefresh}
              disabled={isBusy}
            />
          }
        />
      );
    }

    if (!hasPops) {
      return (
        <EmptyState
          icon={<Clapperboard size={28} />}
          title={activeSearch ? "No Pops found" : "No Pops yet"}
          description={
            activeSearch ? "Try another search." : "Your Pops library is empty."
          }
        />
      );
    }

    return (
      <ContentGrid
        items={popItems}
        columns={6}
        tabletColumns={3}
        mobileColumns={2}
        gap={16}
        showEmpty={false}
        renderItem={renderPop}
      />
    );
  };

  return (
    <ViewContainer>
      <ViewHeader
        title={`My Pops (${total || 0})`}
        description="Manage your Pops."
      />

      <div className="creator-pops-view__search">
        <SearchField
          placeholder="Search your Pops..."
          value={searchValue}
          onChange={handleSearchChange}
          onEnter={handleSearchSubmit}
        />
      </div>

      {errorMessage && hasPops && (
        <div className="creator-pops-view__error" role="alert">
          <Text as="p" size="sm">
            {errorMessage}
          </Text>
        </div>
      )}

      {renderContent()}

      {!activeSearch && hasMore && hasPops && (
        <div className="creator-pops-view__footer">
          <Button
            value={loadingMore ? "Loading..." : "Load More"}
            variant="primary-outlined"
            onClick={loadMore}
            disabled={isBusy}
          />
        </div>
      )}

      <FormModal isOpen={activeModal === "edit"} onClose={handleCloseModal}>
        {activeModal === "edit" && selectedPopTag && (
          <CreatorPopEditForm
            username={username}
            popTag={selectedPopTag}
            onClose={handleCloseModal}
            onSuccess={handleRefresh}
          />
        )}
      </FormModal>

      {isPlayerOpen && (
        <CreatorPopsFeed
          key={selectedPopId}
          pops={popItems}
          initialPopId={selectedPopId}
          fullscreen
          onClose={handleClosePop}
          hasMore={!activeSearch && hasMore}
          loadingMore={loadingMore}
          onLoadMore={handleFeedLoadMore}
          onMoreClick={handleMoreClick}
          onVideoError={handleVideoError}
          showCreator
          showActions
        />
      )}
    </ViewContainer>
  );
}
